import styled from '@emotion/styled';
import React from 'react';
import {
  mixinOverflowY,
  mixinSVGIcon,
  mixinSVGIconSmall,
} from 'webapp-lib/styling';

import {default as ListItem} from '#/List/Item';
import {StyledListSection} from '#/List/Section/styles';
import {StyledList} from '#/List/styles';
import {
  StyledHeader as PlaceholderHeader,
  StyledPlaceholder,
} from '#/Placeholder/styles';
import {StyledSearchBar} from '#/SearchBar/styles';

import {BaseInput, borderColor} from '../../base-input-styles';

import {Props} from '.';

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export enum StartingPoint {
  Dropdown,
  Icon,
  Input,
}

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const Dropdown = styled('div', {
  shouldForwardProp: (prop: string): boolean =>
    !['empty', 'startingPoint', 'hasSuggestedItems'].includes(prop),
})<{empty?: boolean; startingPoint: StartingPoint; hasSuggestedItems: boolean}>`
  ${StyledList}:last-of-type {
    ${({empty}): string => (empty ? 'flex-shrink: 0;' : '')}
  }

  display: flex;
  z-index: var(--zindex-dropdown);
  border: var(--regular-border-width) solid var(--color-state-active);
  flex-direction: column;
  background: var(--color-core-white);
  ${({startingPoint}): string =>
    startingPoint === StartingPoint.Icon ? 'max-width: 480px;' : ''}

  ${StyledPlaceholder} {
    margin: auto;
    width: 100%;
    word-break: break-word;
    padding-left: var(--gutter-little);
    padding-right: var(--gutter-little);

    ${PlaceholderHeader} {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-highlight);
      text-align: center;
      margin: var(--gutter-little);
      color: var(--color-core-primary);
    }

    > svg {
      fill: var(--color-state-active);
      height: max-content;
    }
  }

  ${StyledSearchBar} {
    width: calc(100% - 16px);
    margin: var(--gutter-little);
  }

  ${StyledList} {
    user-select: none;
    margin: 0;
    list-style: none;
    ${mixinOverflowY}
    height: auto;
    max-height: 320px;

    ${StyledListSection} {
      border-color: var(--color-state-active);
      margin: 0;
      padding-top: 0;
    }

    :focus {
      outline: none;
    }

    ::-webkit-scrollbar {
      border: 8px solid transparent;
      background-clip: content-box;
      margin-right: var(--gutter-little);
      display: none;
      width: 20px;
    }

    ::-webkit-scrollbar-thumb {
      border: 8px solid transparent;
      background-clip: content-box;
      min-height: 30px;
    }

    :hover {
      ::-webkit-scrollbar {
        display: initial;
      }
    }

    :first-of-type {
      ${({hasSuggestedItems}): string =>
        hasSuggestedItems ? 'max-height: 200px;' : ''}
    }
  }
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const HeaderLabel = styled.div<{displayingPlaceholder: boolean}>`
  font-weight: var(--font-weight-thin);
  word-break: break-word;
  white-space: pre;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({displayingPlaceholder}): string =>
    displayingPlaceholder ? `color: var(--color-core-black);` : ''};

  svg {
    margin: var(--gutter-very-tiny);
  }
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const Header = styled.div<{disabled?: boolean}>`
  ${({disabled}): string => (disabled ? 'cursor: not-allowed;' : '')}

  svg {
    ${mixinSVGIconSmall}
    fill: var(--color-core-link);
  }
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const InputHeader = styled(BaseInput)<{disabled?: boolean}>`
  ${({disabled}): string => (disabled ? 'cursor: not-allowed;' : '')}

  svg {
    ${mixinSVGIcon}
    flex-shrink: 0;
    fill: ${borderColor()};
  }

  :hover svg {
    fill: ${borderColor({hovered: true})};
  }

  :focus svg {
    fill: ${borderColor({focused: true})};
  }
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledSelectBox = styled('div', {
  shouldForwardProp: (prop) => !['as', 'startingPoint'].includes(prop),
})<Props>`
  outline: none;
  ${({disabled}): string => (disabled ? '' : 'cursor: pointer;')}
  color: var(--color-core-link);

  ${Header} {
    ${({startingPoint}): string =>
      startingPoint === StartingPoint.Icon
        ? 'border: var(--focus-border-width) solid transparent;'
        : ''}
  }

  ${Header}:focus {
    border-radius: ${({startingPoint}): number =>
      startingPoint === StartingPoint.Icon ? 4 : 2}px;
    ${({startingPoint}): string =>
      startingPoint === StartingPoint.Icon
        ? `border: var(--focus-border-width) solid var(--color-core-keyboardfocus);`
        : `box-shadow: 0px 0 0px var(--focus-border-width) var(--color-core-keyboardfocus);`}
  }

  ${Header}, ${InputHeader} {
    display: flex;
    ${({disabled}): string => (disabled ? '' : 'cursor: pointer;')}
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${({startingPoint}): string =>
      startingPoint === StartingPoint.Input
        ? 'var(--gutter-little) var(--gutter) var(--gutter-little) var(--gutter-small)'
        : startingPoint === StartingPoint.Icon
          ? 'var(--gutter-tiny)'
          : '0'};
  }

  ${InputHeader} {
    ${({disabled}): string =>
      disabled ? `background-color: var(--color-core-grey-light);` : ''}
    ${({disabled}): string =>
      disabled ? `color: var(--input-color-disabled);` : ''}
  }

  ${HeaderLabel} {
    ${({startingPoint}): string =>
      startingPoint === StartingPoint.Icon ? 'line-height: 0;' : ''}
  }

  ${
    ({rightAligned}) =>
      /* eslint-disable @typescript-eslint/restrict-template-expressions */
      rightAligned
        ? `
      justify-content: flex-end;
      text-align: right;
    
      && ${Header}, ${InputHeader} {
        justify-content: flex-end;
      }
    `
        : ''
    /* eslint-enable @typescript-eslint/restrict-template-expressions */
  }
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledSelectBoxSection = styled.div`
  padding: var(--gutter) 0 var(--gutter-tiny) var(--gutter);
  color: var(--color-core-primary);
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledSelectBoxTopSection = styled(StyledSelectBoxSection)`
  padding-top: var(--gutter-little);
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledSelectBoxItem = styled(
  ListItem as React.ComponentType<object>
)`
  display: unset;
  height: unset;
  padding-top: var(--gutter-tiny);
  padding-bottom: var(--gutter-tiny);
  white-space: nowrap;
` as unknown as typeof ListItem;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledSelectBoxItemTitle = styled.div`
  padding-bottom: var(--gutter-tiny);
  color: var(--color-core-primary);
  font-weight: var(--font-weight-thin);
  font-size: var(--font-size-tiny);
`;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledSelectBoxItemText = styled.div``;

/**
 * @deprecated Will be removed in LEXIAN-4297. Please use the components from webapp-lib instead.
 */
export const StyledClearButtonWrapper = styled.div`
  padding: var(--gutter-tiny);
  svg {
    width: 8px;
    height: 8px;
  }
`;
